import LoginForm from "@/components/login-form"
import MobileLoginForm from "@/components/mobile-login-form";
import { WithTranslation, withTranslation } from 'react-i18next';
import Welcome from "@/components/welcome";
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { Theme, THEME, THEME_ORDER, STAGE_W, STAGE_H } from "@/components/login-theme";
import { CompetitionRulesDialog } from "@/components/competition-rules-dialog";
// 橙色四色轮盘：走打包导入(带 hash)而非 public 绝对路径,避免部署环境取不到静态资源时破图
import themeWheel from "@/assets/images/theme-wheel.svg";

// 响应式断点对齐原项目：用 Tailwind `lg`(1024px) 纯 CSS 切换布局——
// <1024px 显示移动端流式布局，≥1024px 显示 1920×1080 等比缩放画布。

// 语言切换：胶囊轨道 + 白色滑块（设计稿 frame(1725,28) 139×39）
// variant='mobile' 时去掉绝对定位，改为随顶部栏流式排布（移动端只保留语言切换）。
function LangToggle({ chip, variant = 'desktop' }: { chip: string; variant?: 'desktop' | 'mobile' }) {
  const { i18n } = useTranslation();
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');

  const seg = (active: boolean): React.CSSProperties => ({
    height: 31,
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 28.5,
    background: active ? '#FFFFFF' : 'transparent',
    color: '#000',
    fontSize: 14,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
    border: 0,
  });

  // 1920 设计稿：胶囊容器 139×39（padding 4 + gap 4 + 两个 segment 自适应）
  const container: React.CSSProperties = variant === 'mobile'
    ? { height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip }
    : { position: 'absolute', left: 1725, top: 28, width: 139, height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip };

  return (
    <div style={container}>
      <button type="button" onClick={() => i18n.changeLanguage('en')} style={seg(isEn)}>English</button>
      <button type="button" onClick={() => i18n.changeLanguage('zh-HK')} style={seg(!isEn)}>繁體</button>
    </div>
  );
}

// 主题切换器（设计稿 frame(1505,28)）：当前配色在最左侧，其后竖线分隔，再排列其余三个配色圆点
function ThemeSwitcher({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }) {
  const others = THEME_ORDER.filter((x) => x !== theme);

  const swatch = (th: Theme, size: number) =>
    th === 'orange' ? (
      <img src={themeWheel} alt="" style={{ width: size, height: size, display: 'block' }} />
    ) : (
      <span style={{ width: size, height: size, borderRadius: '50%', background: THEME[th].dot, display: 'block' }} />
    );

  return (
    <div style={{ position: 'absolute', left: 1505, top: 28, height: 39, display: 'flex', alignItems: 'center', gap: 13, padding: '0 12px', borderRadius: 28.5, background: '#FFFFFF' }}>
      <button type="button" aria-label={`current theme ${theme}`} style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
        {swatch(theme, 26)}
      </button>
      <span style={{ width: 1, height: 29, background: '#F3F3F4' }} />
      {others.map((th) => (
        <button
          key={th}
          type="button"
          onClick={() => onChange(th)}
          aria-label={`switch to ${th} theme`}
          className="transition-transform hover:scale-110"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}
        >
          {swatch(th, 24)}
        </button>
      ))}
    </div>
  );
}

function LoginPage({ t }: WithTranslation) {
  const [theme, setTheme] = useState<Theme>('orange');
  const colors = THEME[theme];

  // 将 1920×1080 画布缩放至当前视口（仅 ≥lg 画布需要）。
  // 视口比 16:9 更宽时（典型：>1920px / 超宽屏），等比居中会在左右留下大片空白；
  // 此时改为「按高度缩放 + 多出的横向宽度灌入画布中部空白」——左侧卡片贴左、右侧表单贴右，
  // 元素尺寸与纵向位置 1:1 不变，只把本就为空的中段拉宽，铺满视口、消除两侧留白（对齐原项目自适应思路）。
  // 视口比 16:9 更窄/更高时，维持原等比居中（宽度铺满，可能出现上下留白）。
  const [{ scale, shift }, setLayout] = useState({ scale: 1, shift: 0 });
  useEffect(() => {
    const update = () => {
      const sW = window.innerWidth / STAGE_W;
      const sH = window.innerHeight / STAGE_H;
      if (sW >= sH) {
        setLayout({ scale: sH, shift: window.innerWidth / sH - STAGE_W });
      } else {
        setLayout({ scale: sW, shift: 0 });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const pillStyle = (bg: string): React.CSSProperties => ({
    position: 'absolute',
    top: 28,
    width: 90,
    height: 39,
    borderRadius: 28.5,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 400,
    color: '#000',
    border: 0,
    cursor: 'pointer',
  });

  return (
    <>
      {/* 移动端(<lg)：顶部语言切换 / 中间登录表单 / 底部两个机构 logo（垂直流式，可滚动）。
          用 Tailwind 类控制 display，配合 lg:hidden 纯 CSS 切换（不能用内联 display，否则会盖过 lg:hidden）。 */}
      <div className="lg:hidden fixed inset-0 flex flex-col overflow-y-auto" style={{ backgroundColor: colors.pageBg }}>
        {/* 顶部：仅语言切换，右对齐 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px' }}>
          <LangToggle chip={colors.chip} variant="mobile" />
        </div>

        {/* 中间：登录表单（垂直居中，限制最大宽度） */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '8px 24px 24px' }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            <MobileLoginForm colors={colors} />
          </div>
        </div>

        {/* 底部：UGC + 教大 logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, padding: '20px 16px 32px' }}>
          <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 28, width: 'auto' }} />
          <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 28, width: 'auto' }} />
        </div>
      </div>

      {/* 桌面端(≥lg)：1920×1080 缩放画布；shift 为超宽屏下灌入中部的额外横向宽度 */}
      <div className="hidden lg:flex fixed inset-0 items-center justify-center overflow-hidden" style={{ backgroundColor: colors.pageBg }}>
        <div style={{ width: STAGE_W + shift, height: STAGE_H, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', flex: '0 0 auto' }}>
        {/* 左侧卡片（贴左，坐标不变） */}
        <Welcome theme={theme} colors={colors} />

        {/* 右侧元素整体右移 shift（贴右）：内部仍沿用设计稿 1920 坐标，仅由本容器整体偏移 */}
        <div style={{ position: 'absolute', left: shift, top: 0, width: STAGE_W, height: STAGE_H }}>
          {/* 顶部胶囊：關於我們 / 活動介紹 （frame x=1041 / 1149） */}
          <button type="button" style={{ ...pillStyle(colors.pillAbout), left: 1041 }} className="hover:brightness-[0.97]">
            {t("login.about-us")}
          </button>
          <CompetitionRulesDialog>
            <button type="button" style={{ ...pillStyle(colors.pillActivity), left: 1149 }} className="hover:brightness-[0.97]">
              {t("login.activities")}
            </button>
          </CompetitionRulesDialog>

          {/* 主题切换器 + 语言切换 */}
          <ThemeSwitcher theme={theme} onChange={setTheme} />
          <LangToggle chip={colors.chip} />

          {/* 右侧表单 */}
          <LoginForm theme={theme} colors={colors} />

          {/* 右下角机构 Logo：UGC + 教大（frame UGC@(1513,1019) 204×33，教大@(1758,1019) 106×33） */}
          <div style={{ position: 'absolute', left: 1513, top: 1019, display: 'flex', alignItems: 'center', gap: 41 }}>
            <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 33, width: 'auto' }} />
            <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 33, width: 'auto' }} />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default withTranslation()(LoginPage);
